// Workflow and service-related type definitions

import type { Node, Edge } from '@vue-flow/core';

/** Execution status as used throughout the builder — mirrors the C# ServiceStatus enum. */
export type ServiceRunStatus = 'Succeeded' | 'Failed' | 'Running' | 'Warning' | 'Pending';

const _SERVICE_RUN_STATUSES = new Set<ServiceRunStatus>(['Succeeded', 'Failed', 'Running', 'Warning', 'Pending']);

/** Narrows an unknown value to ServiceRunStatus. */
export function isServiceRunStatus(s: unknown): s is ServiceRunStatus {
  return typeof s === 'string' && _SERVICE_RUN_STATUSES.has(s as ServiceRunStatus);
}

/** The terminal run statuses — a run/service in one of these has settled and produces no more frames.
 *  'Running'/'Pending' are the two non-terminal states. Single source of truth for "still going" checks. */
export const FINISHED_RUN_STATUSES = ['Succeeded', 'Failed', 'Warning'] as const;

const _FINISHED_RUN_STATUSES = new Set<string>(FINISHED_RUN_STATUSES);

/** Whether a run/service status is terminal (settled). Accepts a raw string for convenience. */
export function isFinishedRunStatus(s: string | undefined | null): boolean {
  return !!s && _FINISHED_RUN_STATUSES.has(s);
}

/** A small state label on a builder canvas node: "Component", "3 validators". */
export interface NodeBadge {
  /** Bootstrap icon classes, such as "bi bi-puzzle". */
  icon: string;
  text: string;
}

/** Shape of a VueFlow node's data object in the stacked workflow view. */
export interface ServiceNodeData {
  label: string;
  /** Permanent service-type label — always shown as muted text (e.g. "HTTP Request"). */
  subtitle?: string;
  /** Execution note — set in history/run mode only, shown coloured by status (e.g. "completed in 1.2s"). */
  executionNote?: string;
  serviceType?: string;
  disabled?: boolean;
  canvasMode?: 'editor' | 'history' | 'run';
  isFirstNode?: boolean;
  isLeafNode?: boolean;
  flowDirection?: 'TB' | 'LR';
  /** Live execution data — present during/after a live run, absent in editor and history modes. */
  executionData?: any;
  /** Execution overlay — present in history/run mode, absent in editor mode. */
  run?: ServiceHistory;
  /** State badges. Set for editor nodes by BuilderView. */
  badges?: NodeBadge[];
  // Editor action callbacks — injected by BuilderView, absent in history/run mode
  onAddService?: (parentNodeId: string) => void;
  onRunService?: (nodeId: string) => void;
  onToggleDisabled?: (nodeId: string) => void;
  onDeleteService?: (nodeId: string) => void;
  onRenameService?: (nodeId: string) => void;
  onDuplicateService?: (nodeId: string) => void;
  onEditParents?: (nodeId: string) => void;
}

/**
 * Edge data for the animated history edges (see AnimatedEdge.vue / edgeStyleForStatus).
 * A live run turns the flowing dot on with a colour; a finished run leaves it off.
 */
export interface RunCanvasEdgeData {
  showFlowingIcon?: boolean;
  iconColor?: string;
  animationDuration?: string;
}

/** A laid-out VueFlow node on the run-history canvas — its data is a {@link ServiceNodeData}. */
export type RunCanvasNode = Node<ServiceNodeData>;

/** A styled VueFlow edge on the run-history canvas. */
export type RunCanvasEdge = Edge<RunCanvasEdgeData>;

export interface KeyValuePair<K, V> {
  key: K;
  value: V;
}

export interface StatusCount {
  type: string;
  count: number;
}

export interface ServiceRun {
  statusCount: StatusCount[];
  status: string;
}

export interface Service1 {
  serviceId: string;
  id: string;
  serviceLabel: string;
  x: string;
  y: string;
  parentServiceIds: string[];
  parentIds: string[];
  [key: string]: any;
  imagePath: string;
  viewComponent: string;
  serviceRun: ServiceRun;
  serviceRunSummary: string;
}

export interface RunStatus {
  workflowStatus: string;
  description: string;
  name: string;
}

export interface WorkflowRun {
  name: string;
  workflowOverallProgress: string;
  created: Date;
  runStatus: RunStatus;
}

export interface WorkflowRunSummary {
  id: string;
  dateTime: Date;
  runStatus: RunStatus;
  tooltipSummary: string;
}

export interface WorkflowRunInput {
  k: string;
  v: string;
}

export interface RunNowInput {
  key: string;
  value: string;
  canDelete: boolean;
}

export interface log {
  id: string;
  info: string;
  created: string;
  rawData: string;
}

export interface WorkflowRunLogs {
  logs: log[];
  instanceLogs: log[];
}

export interface WorkflowSettings {
  workflowId: string;
  workflowInputs: KeyValuePair<string, string>[];
  workflowAttributes: KeyValuePair<string, string>[];
  wizardInputs: KeyValuePair<string, string>[];
}

export interface WorkflowAttributes {
  workflowAttributes: KeyValuePair<string, string>[];
}

export interface ServiceDetail {
  id: string;
  name: string;
}

export interface ServiceRunDetail {
  serviceId: string;
  serviceName: string;
}

export interface DropdownItem {
  id: string;
  text: string;
}

export interface WorkflowDropdownItem {
  id: string;
  text: string;
  customFieldProductName: string;
  children: WorkflowDropdownItem[];
}

export interface ErrorsByService {
  name: string;
  metaData?: object;
  failed?: number;
  warning?: number;
  success?: number;
  MetaData?: { Name: string; ProductName: string };
}

export interface GenericKeyValue {
  k: string;
  v: string;
}

export interface ProjectWorkflowView {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  currentVersion?: string;
  status: string | '';
  lastPublished?: string | Date;
  wizardStepCount?: number;

  // Display/UI fields (may come from API or be computed)
  version?: string;
  versionType?: string;
  hasOverride?: boolean;
  baseProductVersion?: string;
  isVersionLocked?: boolean;
  isPublished?: boolean;
  lastRun?: string | null;
  successRate?: number;
  isCustomized?: boolean;
  hasDraftChanges?: boolean;
  isActive?: boolean;
}

// Enumerations
export enum WorkflowStatus {
  Draft = 'Draft',
  Published = 'Published'
}

export enum DataType {
  JSON = 'JSON',
  XML = 'XML',
  HTML = 'HTML',
  PLAINTEXT = 'PLAINTEXT',
  BINARY = 'BINARY'
}

// Wizard-related interfaces
export interface WizardValidationRule {
  type: string; // "Required", "MinLength", "Email", etc.
  parameters: Record<string, any>;
  errorMessage: string;
}

/** Phase 1b — per-field upgrade policy. */
export type UpgradePolicy = "Preserve" | "Overwrite";

export interface WizardStep {
  id: string;
  name: string;
  componentType: string; // e.g., "TextInput", "Dropdown", "ConnectionPicker"
  properties: Record<string, any>;
  validationRules: WizardValidationRule[];
  isRequired: boolean;
  order: number;
  // Defaults AND upgrade policy are per-field — they live in
  // properties.vueformConfig.schema[fieldName].{default,onUpgrade}.
}

export interface WizardDefinition {
  id: string;
  name: string;
  description: string;
  steps: WizardStep[];
  configuration: Record<string, any>;
  isActive: boolean;
}

// Service-related interfaces
export interface ServicePathMetadata {
  substitutionPath: string;
  jsonPath: string;
  referencedServiceId: string;
  validationError: string;
}

export interface ParseXmlSettings {
  stripAllNamespaces: boolean;
  getXElementInsteadOfValue: boolean;
}

/** A leaf whose value is a document in its own right — a DataSet returned as one escaped string. */
export interface NestedDocumentSettings {
  path: string[];
  format: "JSON" | "XML";
}

export interface OutputParseSettings {
  autoParse: boolean;
  xmlSettings: ParseXmlSettings;
  autoIteratorPath: string;
  nestedDocument?: NestedDocumentSettings;
}

export interface ServiceEventHandlers {
  onSuccess: string[];
  onError: string[];
  onBeforeSend: string[];
}

/** Where a service's node sits on the builder canvas. Part of the workflow definition, so a
 *  hand-arranged layout is shared across editors and survives refetch, reload, and publish. */
export interface CanvasLayout {
  x: number;
  y: number;
}

export interface Service {
  id: string;
  name: string;
  serviceModelType: IServiceModel; // Polymorphic service model (use specific types for type narrowing)
  parentServiceIds: string[];
  waitForServiceIds: string[];
  serviceFilterExpression: string;
  outputParseSettings: OutputParseSettings;
  eventHandlers: ServiceEventHandlers;
  pathMetadata: ServicePathMetadata[];
  /** Phase 4 — when true, this service appears in the product's Components library for subscribers. */
  isComponent?: boolean;
  /** Builder-canvas position. Optional: a pre-CanvasLayout service (or one never arranged) has none,
   *  and the canvas falls back to auto-layout for it. */
  canvasLayout?: CanvasLayout;
}

/**
 * A typed connection requirement declared on a workflow (ADR-030, model D). The name is the
 * identity — `(name, connectionTemplateId)` is unique per workflow. A service auth-links to a slot
 * by setting `connectionName`; the project resolves the slot to a connection instance.
 */
export interface ConnectionSlot {
  name: string;
  connectionTemplateId: string;
}

// Main Workflow interface (based on WorkflowSnapshot)
export interface Workflow {
  id: string;
  projectId: string;
  name: string;
  currentVersion: string;
  status: WorkflowStatus;
  lastPublished?: Date | string;
  wizardDefinitions: WizardDefinition[];
  services: Service[];
  connectionSlots: ConnectionSlot[];
  rootServiceId?: string;
}

// Service Model Types (Polymorphic IServiceModel implementations)

export interface IServiceModel {
  // Base interface for all service models
  $type?: string; // Discriminator for polymorphic deserialization
}

export interface NoneServiceModel extends IServiceModel {
  $type?: 'none';
}

// Base HTTP service model properties (shared by HttpServiceModel, HttpServiceModelPaging, WebhookServiceModel)
export interface HttpServiceModelBase extends IServiceModel {
  /** Name of the workflow ConnectionSlot this service authenticates through (ADR-030). Unset = no managed connection. */
  connectionName?: string;
  targetUrl: string;
  httpMethod: string; // HttpMethod enum as string (GET, POST, PUT, DELETE, etc.)
  httpVersion: HttpVersionEnum;
  requestHeaders: Record<string, string[]>;
  bodyMediaTypeHeader: string;
  /** Absent when the request has no body: the API writes a "none" body as null and leaves the property out. */
  bodyContent?: IBodyContent;
  validatorPipeline: ValidatorRule[];
  /** When true, a response with an error status (≥400) fails the transaction unless a validator rule
   *  overrides it (#6022). When false, the author owns classification via rules. */
  regardHttpErrorAsFatal: boolean;
  disableChunking: boolean;
  /** Whether this request is safe to send again (author's assertion). On by default. Governs retry of
   *  possibly-processed failures (5xx, dropped response, timeout); connection failures that never landed,
   *  and 429/503, always retry regardless. */
  requestIsRepeatable: boolean;
  requestTimeout: string; // TimeSpan as string (e.g., "00:01:40")
}

export interface HttpServiceModel extends HttpServiceModelBase {
  $type?: 'http';
}

/** What the next page's request changes. A part left unset is unchanged, so the delta merges over the
 *  request as authored rather than replacing it — a Concur pull keeps its own `count=1000`. The delta
 *  merges over the original request every page, never over the previous page's. */
export interface NextPageRequest {
  /** The whole address of the next page, where the vendor hands one back. */
  url?: string;
  /** Query parameters to set on the next request, by name. Each value is an expression. */
  query: Record<string, string>;
  /** The next request's whole body, as one expression. A bare expression cannot mix literal text with
   *  slots, so a mostly-literal JSON body is written as a JSON-building expression. */
  body?: string;
}

export interface HttpServiceModelPaging extends HttpServiceModelBase {
  $type?: 'httpPaging';
  nextPage?: NextPageRequest;
  /** A BBEL predicate, true while another page follows, evaluated once per page against `response.*`
   *  and `paging.page`. Stored bare — no `{{ }}` — so the single pre-render leaves it for the service
   *  to evaluate per page. The publish gate refuses a paged service without one. */
  hasNextPageExpression: string;
  /** Pages this service may fetch before it gives up. Reaching it fails the transaction rather than
   *  publishing what it has. */
  maxPages: number;
}

// The address a vendor posts the answer to is minted per transaction and reached with
// {{ system.webhookUrl }} — no field carries it.
export interface WebhookServiceModel extends HttpServiceModelBase {
  $type?: 'webhook';
}

export interface JavaScriptServiceModel extends IServiceModel {
  $type?: 'javaScript';
  javaScript: string;
}

export interface ExpressionServiceModel extends IServiceModel {
  $type?: 'expression';
  expression: string;
}

export enum SftpOperation {
  Upload = 'Upload',
  Download = 'Download',
  List = 'List',
  Delete = 'Delete',
  Rename = 'Rename',
}

export interface SftpServiceModel extends IServiceModel {
  $type?: 'sftp';
  /** Name of the workflow ConnectionSlot this step authenticates through (ADR-030). */
  connectionName?: string;
  operation: SftpOperation;
  /** Remote target — a file path (Download/Delete/Rename) or a directory (List). BBEL. */
  remotePath: string;
  /** Upload target file name under the remote directory (BBEL). Upload only. */
  remoteFileName?: string;
  /** Overwrite an existing remote file on Upload; when false an existing file fails the op. */
  overwrite: boolean;
  /** Rename destination path (BBEL). Rename only. */
  newPath?: string;
  /** What Upload sends (ADR-034) — a binary reference (streamed verbatim) or a BBEL expression
   *  (inlined as the file body). Upload only. */
  source?: FieldValue;
}

export interface SplitServiceModel extends IServiceModel {
  $type?: 'split';
  /** Binary reference to an upstream service's stored array (ADR-034); fanned out 1→N. An
   *  expression source is rejected at publish — an inline array cannot be streamed. */
  source?: FieldValue;
}

export interface BatchServiceModel extends IServiceModel {
  $type?: 'batch';
  source?: FieldValue;
  maxRows?: number | null;
  maxBytes?: number | null;
}

/** One array in a flatten's chain: the member chain down to it, the row key its fields sit under, and the
 *  members it keeps (empty keeps every non-array member). */
export interface FlattenLevel {
  path: string[];
  flattenedKey: string;
  fields: string[];
}

export interface FlattenServiceModel extends IServiceModel {
  $type?: 'flatten';
  source?: FieldValue;
  levels: FlattenLevel[];
}

export interface GroupServiceModel extends IServiceModel {
  $type?: 'group';
  source?: FieldValue;
  /** The member chain to the field rows are grouped by. */
  key: string[];
  head: string[];
  fields: string[];
}

export interface JoinServiceModel extends IServiceModel {
  $type?: 'join';
  in?: FieldValue;
  inKey: string[];
  /** A BBEL template rendered per row. */
  key: string;
  include: 'Matched' | 'Everything';
}

export interface FilterServiceModel extends IServiceModel {
  $type?: 'filter';
  in?: FieldValue;
  inKey: string[];
  /** A BBEL template rendered per row. */
  key: string;
  include: 'Matched' | 'Unmatched';
}

export interface ConcatServiceModel extends IServiceModel {
  $type?: 'concat';
  parts: FieldValue[];
}

export interface AggregateServiceModel extends IServiceModel {
  $type?: 'aggregate';
  /** Member names from the root of one source transaction's output to the number. */
  path: string[];
  operation: 'Sum' | 'Count' | 'Min' | 'Max' | 'Avg';
}

export interface SubWorkflowServiceModel extends IServiceModel {
  $type?: 'subWorkflow';
  workflowId: string;
  /** The child run's inputs by key; each value is a BBEL template. */
  inputs: Record<string, string>;
}

// Rejoin, Wait and Return read the service before them, so there is nothing to author.
export interface RejoinServiceModel extends IServiceModel {
  $type?: 'rejoin';
}

export interface WaitServiceModel extends IServiceModel {
  $type?: 'wait';
}

export interface ReturnServiceModel extends IServiceModel {
  $type?: 'return';
}

// HTTP-related enums and types

export enum HttpVersionEnum {
  Version10 = 'Version10',
  Version11 = 'Version11',
  Version20 = 'Version20',
  Version30 = 'Version30'
}

// HTTP Methods (based on System.Net.Http.HttpMethod)
export type HttpMethod =
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'DELETE'
  | 'PATCH'
  | 'HEAD'
  | 'OPTIONS'
  | 'TRACE'

export const HttpMethods = {
  GET: 'GET' as HttpMethod,
  POST: 'POST' as HttpMethod,
  PUT: 'PUT' as HttpMethod,
  DELETE: 'DELETE' as HttpMethod,
  PATCH: 'PATCH' as HttpMethod,
  HEAD: 'HEAD' as HttpMethod,
  OPTIONS: 'OPTIONS' as HttpMethod,
  TRACE: 'TRACE' as HttpMethod,
} as const

// Array of HTTP methods for iteration (e.g., in v-for loops)
export const HttpMethodsList: HttpMethod[] = [
  'GET',
  'POST',
  'PUT',
  'DELETE',
  'PATCH',
  'HEAD',
  'OPTIONS',
  'TRACE',
]

// Field values (ADR-034) — a binary-capable field is either a BBEL expression or a picker-authored
// reference to an upstream service's stored bytes (streamed by the engine, never evaluated).

export type FieldValue = ExpressionValue | BinaryValue;

export interface ExpressionValue {
  $type: 'expression';
  expression: string; // BBEL template
}

export interface BinaryValue {
  $type: 'binary';
  reference: BinaryReference;
}

/** Named projection of an upstream service — wire form of the ServiceProjection enum (PascalCase
 *  via the global JsonStringEnumConverter). Binary references only ever use Output or Raw. */
export type ServiceProjection = 'Output' | 'Raw' | 'Headers' | 'StatusCode' | 'Iteration';

/** Picker-authored reference to an upstream service's stored bytes (ADR-034). {@link path} is a
 *  bare member chain (no indices, no computed access); empty binds the whole output. */
export interface BinaryReference {
  serviceId: string;
  projection: ServiceProjection;
  path: string[];
}

// Body Content Types (Polymorphic IBodyContent implementations)

export interface IBodyContent {
  $type?: string; // Discriminator
}

export interface NoneBodyContent extends IBodyContent {
  $type?: 'none';
}

export interface TextBodyContent extends IBodyContent {
  $type?: 'text';
  textContent: string; // Fluid template string
}

export interface MultipartFormDataBodyContent extends IBodyContent {
  $type?: 'multipart';
  multipartFormContent: MultiPartBodyModel[];
}

export interface UrlEncodedBodyContent extends IBodyContent {
  $type?: 'urlEncoded';
  bodyFormUrlEncodedContent: KeyValuePair<string, string>[];
}

/** The HTTP binary body is always a reference to upstream bytes, streamed by the engine (ADR-034). */
export interface BinaryBodyContent extends IBodyContent {
  $type?: 'binary';
  source: BinaryReference;
}

/** One multipart/form-data part: a text part (expression) or a file part (binary reference). */
export interface MultiPartBodyModel {
  name: string;
  fileName?: string;
  value: FieldValue;
}

// Validator Types (#6022) — an authored response-validation rule is a Condition tree + an action.
// The tree is the persisted source of truth; the backend compiles it to a single BBEL boolean.

/** A rule's condition: a group of sub-conditions, a single comparison, or raw BBEL (the escape hatch).
 *  `$type` discriminators match PolymorphicTypeRegistry (`group`/`predicate`/`expression`). */
export type Condition = ConditionGroup | PredicateLeaf | ExpressionLeaf;

export interface ConditionGroup {
  $type: 'group';
  combinator: Combinator;
  rules: Condition[];
}

export interface PredicateLeaf {
  $type: 'predicate';
  /** A bare scope path, e.g. `self.statusCode`. */
  field: string;
  operator: PredicateOperator;
  /** A literal (JSON-encoded by the compiler) unless authored as a `{{ expression }}`. */
  value: string;
}

export interface ExpressionLeaf {
  $type: 'expression';
  expression: string; // raw BBEL
}

export enum Combinator {
  All = 'All', // &&
  Any = 'Any', // ||
}

export enum PredicateOperator {
  Equals = 'Equals',
  NotEquals = 'NotEquals',
  GreaterThan = 'GreaterThan',
  GreaterThanOrEqual = 'GreaterThanOrEqual',
  LessThan = 'LessThan',
  LessThanOrEqual = 'LessThanOrEqual',
  Contains = 'Contains',
  NotContains = 'NotContains',
  StartsWith = 'StartsWith',
  EndsWith = 'EndsWith',
  In = 'In',
  NotIn = 'NotIn',
  IsEmpty = 'IsEmpty',
  IsNotEmpty = 'IsNotEmpty',
  IsValidJson = 'IsValidJson',
  IsNotValidJson = 'IsNotValidJson',
}

export interface ValidatorRule {
  id: string;
  name: string;
  condition: Condition;
  action: RuleAction;
}

export interface RuleAction {
  type: ActionType;
  message: string; // author-facing reason surfaced on fail/warn
  maxRetries: number; // applies to Retry
  retryIntervalSeconds: number | null; // applies to Retry; null leaves the engine's backoff to pick
}

export enum ActionType {
  Proceed = 'Proceed',
  Fail = 'Fail',
  Retry = 'Retry',
  Warn = 'Warn', // continue as success, but flag the run as a warning
}


// Static service model types definition
export interface ServiceModelOption {
  id: string;
  type: string; // The $type discriminator value
  name: string;
  description: string;
  icon: string;
  category: "HTTP" | "Transform" | "Integration" | "Other";
}


export const serviceModelTypes: ServiceModelOption[] = [
  {
    id: "http",
    type: "http",
    name: "HTTP Request",
    description: "Make HTTP requests to external APIs and services",
    icon: "bi bi-globe",
    category: "HTTP",
  },
  {
    id: "httpPaging",
    type: "httpPaging",
    name: "HTTP Paging Request",
    description: "Make paginated HTTP requests with automatic iteration",
    icon: "bi bi-arrow-repeat",
    category: "HTTP",
  },
  {
    id: "webhook",
    type: "webhook",
    name: "Webhook",
    description: "Send an HTTP request; the vendor posts the answer back",
    icon: "bi bi-arrow-down-circle",
    category: "HTTP",
  },
  {
    id: "javaScript",
    type: "javaScript",
    name: "JavaScript",
    description: "Execute custom JavaScript code for data transformation",
    icon: "bi bi-code-slash",
    category: "Transform",
  },
  {
    id: "expression",
    type: "expression",
    name: "Expression",
    description: "Render a BBEL template — text with {{ expression }} slots",
    icon: "bi bi-file-earmark-code",
    category: "Transform",
  },
  {
    id: "sftp",
    type: "sftp",
    name: "SFTP",
    description: "Transfer files via SFTP protocol",
    icon: "bi bi-folder2-open",
    category: "Integration",
  },
  {
    id: "split",
    type: "split",
    name: "Split",
    description: "Fan out — emit one branch per element of an upstream array",
    icon: "bi bi-diagram-3",
    category: "Transform",
  },
  {
    id: "batch",
    type: "batch",
    name: "Batch",
    description: "Cuts an array into batches and runs the next service once per batch.",
    icon: "bi bi-stack",
    category: "Transform",
  },
  {
    id: "flatten",
    type: "flatten",
    name: "Flatten",
    description: "Walks nested arrays and gives one row for each innermost element, with each level's fields under its row key.",
    icon: "bi bi-list-nested",
    category: "Transform",
  },
  {
    id: "group",
    type: "group",
    name: "Group",
    description: "Puts the rows that share a key value into one group and runs the next service once per group.",
    icon: "bi bi-collection",
    category: "Transform",
  },
  {
    id: "join",
    type: "join",
    name: "Join",
    description: "Looks each row up in an array and attaches the record it matches.",
    icon: "bi bi-intersect",
    category: "Transform",
  },
  {
    id: "filter",
    type: "filter",
    name: "Filter",
    description: "Looks each row up in an array and keeps or drops the row by whether it was found.",
    icon: "bi bi-funnel",
    category: "Transform",
  },
  {
    id: "concat",
    type: "concat",
    name: "Concat",
    description: "Writes its parts one after another into one document.",
    icon: "bi bi-file-earmark-plus",
    category: "Transform",
  },
  {
    id: "rejoin",
    type: "rejoin",
    name: "Rejoin",
    description: "Collects the output of every transaction of the service before it into one list.",
    icon: "bi bi-union",
    category: "Transform",
  },
  {
    id: "aggregate",
    type: "aggregate",
    name: "Aggregate",
    description: "Reduces every transaction of the service before it to one number.",
    icon: "bi bi-calculator",
    category: "Transform",
  },
  {
    id: "wait",
    type: "wait",
    name: "Wait",
    description: "Holds the services after it until every transaction of the service before it has completed. It has no output.",
    icon: "bi bi-hourglass-split",
    category: "Transform",
  },
  {
    id: "subWorkflow",
    type: "subWorkflow",
    name: "Sub-workflow",
    description: "Runs another workflow and takes the value at its Return as this service's output.",
    icon: "bi bi-box-arrow-in-down-right",
    category: "Other",
  },
  {
    id: "return",
    type: "return",
    name: "Return",
    description: "Hands the value of the service before it back to the calling workflow, and stops the run if there is more than one row.",
    icon: "bi bi-box-arrow-up-right",
    category: "Other",
  },
  {
    id: "none",
    type: "none",
    name: "None / Placeholder",
    description: "Empty service node with no action",
    icon: "bi bi-dash-circle",
    category: "Other",
  },
];

/** Single source for a service type's display name + icon (keyed by the model $type). Used by the
 *  node card, the editor header, and the workflow list so they never drift. Unknown/absent → a
 *  neutral fallback. */
export function serviceTypeDisplay(type: string | undefined | null): { name: string; icon: string } {
  const option = serviceModelTypes.find((o) => o.type === type);
  return option
    ? { name: option.name, icon: option.icon }
    : { name: "No Service Type", icon: "bi bi-question-circle" };
}


// Run History interfaces
export interface ServiceHistory {
  serviceId: string;
  serviceName: string;
  /** Service-model discriminator ("http", "split", …) — drives the per-type icon in history views. */
  serviceType: string;
  status: ServiceRunStatus;
  /** Absolute transaction counts for the service, final once its status leaves 'Pending'. */
  total: number;
  succeeded: number;
  warning: number;
  failed: number;
  /** Sent, outcome never learned. Terminal, and counted against the service. */
  neverResponded: number;
  /**
   * How many transactions this service is going to have, while it is still creating them — what `total`
   * is climbing towards. Projected from the parents read so far, so it is revised as the service goes,
   * and zero once it has finished, `total` being the answer it was estimating.
   */
  expected: number;
  /** How long this service was worked on, summed over its deliveries — its own duration, not the run's. */
  processingMs: number;
  /** Transactions in flight at once on average; null where nothing was worked. */
  achievedConcurrency: number | null;
  /** What this service spent. Absent on older runs, which recorded no per-service cost. */
  cost?: RunCost;
  parentServiceIds: string[];
  /** Where this service's node sat on the builder canvas when the run started, snapshotted so the
   *  history canvas reads with the arrangement of the workflow that ran. Absent for a service with no
   *  saved position, and for runs recorded before layout was captured — the canvas auto-lays-out then. */
  canvasLayout?: CanvasLayout;
}

/**
 * What a run or one of its services spent. Request units price Cosmos alone, so a run entirely on table
 * storage reads zero there and its store-request count is the cost line instead.
 */
export interface RunCost {
  requestCharge: number;
  storeRequests: number;
  blobBytes: number;
  brokerSends: number;
  /** Requests that left for a vendor — not the same as transactions; one can make several. */
  vendorCalls: number;
  /** Elapsed transaction time, not CPU. Against the service's own duration it gives the concurrency reached. */
  computeMs: number;
  /** Time requests were held for a permit, kept apart by whose limit held them. Summed across requests in
   *  flight at once, so it can exceed the wall clock — read it per request, not as elapsed time. */
  throttledVendorMs: number;
  throttledInternalMs: number;
  throttledOrgMs: number;
  throttledPlatformMs: number;
}

export interface WorkflowRunError {
  // Add properties based on your WorkflowRunError C# class
  // Example structure (adjust based on your actual implementation):
  message: string;
  serviceId?: string;
  timestamp?: Date | string;
}

export interface RunHistory {
  id: string;
  level?: number;
  parentRunId?: string;
  parentServiceId?: string;
  parentTransactionId?: string;
  /** Saved invoking-step name, supplied when expanding the parent run. */
  parentServiceName?: string;
  /** Logical (master) workflow id — matches the id the Workflows list, filters and redirect use. */
  workflowId: string;
  /** The published version whose content ran — for a customized workflow it belongs to the subscriber's fork. */
  workflowVersionId?: string;
  workflowName: string;
  /** The project this run executed under, so an org-wide list can name it. Empty for a run with no
   *  project context (legacy, or an orchestrated run). Also used to guard a deep-linked run against the
   *  builder's current project — an undefined value fails the equality check and is rejected, which is
   *  the safe default. The per-project History tab ignores it. */
  runningProjectId?: string;
  startedAt: Date;
  /** Absent while the run is still going: the API omits null properties rather than sending them. */
  completedAt?: Date | null;
  durationInMilliSeconds: number;
  status: ServiceRunStatus;
  services: ServiceHistory[];
  errors: WorkflowRunError[];
  /** Per-invocation inputs the run was started with, keyed by input name. Prefills the re-run modal. */
  inputs?: Record<string, unknown>;
  /** How long the run waited for a place in its org's pool. Absent while it is still waiting. */
  queueWaitMs?: number | null;
  /** What the whole run spent. Absent on the run list, which does not carry it. */
  cost?: RunCost;
}

// Run transaction explorer (#6009) — per-transaction request/response within a service execution.

/** One row in a service execution's transaction list — generic, service-type-agnostic metadata.
 * Request/response specifics live in the drill-in detail, not the list. */
export interface TransactionSummary {
  id: string;
  status: string; // engine TransactionStatus name (Succeeded | Failed | …)
  created: Date | string;
  attemptCount: number;
  errorMessage?: string | null;
}

/** One page and where the next one starts. `next` is null at the end of the service — there is no
 * total, because counting a service's transactions costs a read of every one of them. */
export interface TransactionPage {
  items: TransactionSummary[];
  next?: string | null;
}

export interface TransactionHeader {
  name: string;
  value: string; // sensitive values arrive already redacted
}

/** Which of the four answers a preview gave, and therefore what to do next. */
export type PreviewOutcome = "None" | "Included" | "Fetchable" | "DownloadLinkOnly";

/**
 * A body, whether it arrived with a detail or from a fetch — one shape either way. `outcome` says what to do:
 * `Included` — show `text`; `Fetchable` — request it; `DownloadLinkOnly` — offer the download; `None` — render
 * nothing.
 */
export interface ContentPreview {
  outcome: PreviewOutcome;
  lengthBytes: number;
  mediaType?: string | null;
  type: string; // DataType name (JSON | XML | …)
  text?: string | null;
  /** The element count of segmented content, when this body is one element of it. */
  elements?: number | null;
}

/** One side of a transaction's HTTP exchange (request OR response). */
export interface TransactionMessage {
  method?: string | null;
  url?: string | null;
  statusCode?: number | null;
  reasonPhrase?: string | null;
  headers: TransactionHeader[];
  body: ContentPreview;
}

export interface TransactionErrorDetail {
  message: string;
  type: string;
  exceptionType?: string | null;
  traceId?: string | null;
}

/** One attempt: one request on the wire and what came back. */
export interface HttpAttempt {
  /** Names this attempt among the transaction's attempts. Reads of it and of its bodies take it as given. */
  id: string;
  sentAt: Date | string;
  receivedAt?: Date | string | null;
  request: TransactionMessage;
  response?: TransactionMessage | null;
  error?: TransactionErrorDetail | null;
}

/** One attempt in a transaction's list of attempts: when it went and how it was answered. */
export interface AttemptSummary {
  /** Names this attempt among the transaction's attempts. Reads of it and of its bodies take it as given. */
  id: string;
  sentAt: Date | string;
  receivedAt?: Date | string | null;
  statusCode?: number | null;
  reasonPhrase?: string | null;
  error?: TransactionErrorDetail | null;
}

/** The workflow version a run pinned: what each service was authored to do when the run started. */
export interface RunWorkflowVersion {
  id: string;
  versionLabel: string;
  services: Service[];
}

/** One page of attempts and where the next one starts; `next` is null on the last page. */
export interface AttemptPage {
  items: AttemptSummary[];
  next?: string | null;
}

/**
 * What one executor's execution looked like — one arm per kind, tagged by `$type` as every other polymorphic
 * shape on this API is. Only the arm that arrived is present, so narrowing on `$type` is what makes the rest of
 * the fields readable.
 */
export type ExecutionDetail =
  | HttpExecution
  | SftpExecution
  | SplitExecution
  | ExpressionExecution
  | SubWorkflowExecution
  | NoExecution;

/**
 * An HTTP execution: how many attempts the transaction made, and the one that settled it. Earlier attempts are
 * read by id.
 */
export interface HttpExecution {
  $type: "http";
  /** How many requests the transaction put on the wire. */
  attemptCount: number;
  /** The attempt that settled the transaction. Null when nothing was recorded. */
  last?: HttpAttempt | null;
}

export interface SftpExecution {
  $type: "sftp";
  operation: string;
  remotePath: string;
  remoteFileName?: string | null;
  newPath?: string | null;
  overwrite: boolean;
  startedAt: Date | string;
}

/** Where a split read from and what came out. The source transaction is where the bytes it parsed live. */
export interface SplitExecution {
  $type: "split";
  sourceServiceId: string;
  sourceTransactionId: string;
  path: string;
  nestedDocument: boolean;
  elementCount: number;
}

export interface ExpressionExecution {
  $type: "expression";
  template: string;
}

/** The child run a sub-workflow started. Everything after `workflowId` is absent when the child run is not found. */
export interface SubWorkflowExecution {
  $type: "subWorkflow";
  childRunId: string;
  workflowId: string;
  workflowName?: string | null;
  status?: string | null;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
}

/** An executor whose whole answer is on the transaction. */
export interface NoExecution {
  $type: "none";
}

/**
 * What a transaction returned, and so what its children resolve references through. `statusCode` is absent for
 * a kind that answers with none — a split returns elements and an expression returns text.
 */
export interface TransactionResult {
  statusCode?: number | null;
  headers: TransactionHeader[];
  body: ContentPreview;
  inbound: boolean;
  fanOut: boolean;
  /** The element count when the result is segmented content. */
  elements?: number | null;
}

export interface TransactionOutputs { count?: number | null; excludedBy: string[]; }
export interface TransactionValidation { ruleName: string; action: string; message: string; }
export interface TransactionParentRef { serviceId: string; transactionId: string; outputIndex: number; }
export interface ConnectionRef { id: string; name?: string | null; }

export interface TransactionDetail {
  id: string;
  serviceId: string;
  runId: string;
  status: string;
  created: Date | string;
  executionStartedAt: Date | string;
  completedAt?: Date | string | null;
  attemptCount: number;
  result: TransactionResult;
  outputs: TransactionOutputs;
  validation?: TransactionValidation | null;
  parents: TransactionParentRef[];
  connection?: ConnectionRef | null;
  cost: RunCost;
  pressure?: string | null;
  error?: TransactionErrorDetail | null;
  execution: ExecutionDetail;
}


